import React, {useState} from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import {apiCreateGroup} from 'constants/Api';
import {ICreateGroupResponse} from 'constants/interfaces';

interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (group: ICreateGroupResponse) => void;
}

export const CreateGroupDialog = ({
  open,
  onClose,
  onCreated,
}: CreateGroupDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (isSubmitting) return;
    setName('');
    setDescription('');
    setIsPrivate(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Group name is required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const created = await apiCreateGroup({
        name: trimmedName,
        description: description.trim() || undefined,
        isPrivate,
      });
      setName('');
      setDescription('');
      setIsPrivate(false);
      onCreated(created);
    } catch (err: any) {
      // graphqlRequestHandler rejects with an array of error message strings
      if (Array.isArray(err)) {
        setError(err.join(' '));
      } else if (typeof err === 'string') {
        setError(err);
      } else {
        setError(err?.message ?? 'Failed to create group. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Group</DialogTitle>
      <DialogContent>
        <TextField
          label="Group Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          variant="outlined"
          fullWidth
          required
          autoFocus
          disabled={isSubmitting}
          style={{marginBottom: 16}}
          inputProps={{maxLength: 100}}
        />
        <TextField
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          disabled={isSubmitting}
          style={{marginBottom: 16}}
          inputProps={{maxLength: 500}}
        />
        <FormControlLabel
          control={
            <Switch
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              color="primary"
              disabled={isSubmitting}
            />
          }
          label="Private group"
        />
        {error && (
          <Typography variant="body2" color="error" style={{marginTop: 12}}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disableElevation
          disabled={isSubmitting || !name.trim()}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isSubmitting ? 'Creating…' : 'Create Group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
